import React, { useState, useEffect } from 'react';
import { 
  Accordion, 
  Card, 
  Form, 
  Dropdown, 
  Button,
  ToggleButton,
  Spinner,
  Offcanvas
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFilter, 
  faMapSigns, 
  faExchangeAlt, 
  faTasks, 
  faComments, 
  faUser, 
  faChevronDown,
  faExclamationTriangle,
  faHome,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { MapFilters } from './MapContainer';
import { getProfessionColor } from '../../utils/mapping';
import { useQuery } from 'react-query';
import api from '../../services/api';
import { showToast } from '../common/ToastContainer';
import { useNavigate, Link } from 'react-router-dom';
import { professionOptions, SelectOption } from '../../utils/options';

interface MapSidebarProps {
  filters: MapFilters;
  onFiltersChange: (filters: MapFilters) => void;
  showSidebar: boolean;
  onCloseMobile?: () => void;
  onGoHomeClick?: () => void;
}

// Interface for the network stats data
interface NetworkStatsData {
    totalUsers: number;
    userConnections: number;
    openTasks: number;
    // Add more fields if the backend provides them
}

// API Response Types for Network Stats
interface NetworkStatsSuccessResponse {
    success: true;
    data: NetworkStatsData;
}
interface NetworkStatsErrorResponse {
    success: false;
    message: string;
}
type NetworkStatsApiResponse = NetworkStatsSuccessResponse | NetworkStatsErrorResponse;

// Simplified Task interface based on expected API response for the list
interface TaskSummary {
  id: number;
  title: string;
  createdAt: string; // Assuming ISO string date
  owner?: { // Owner might be included
    id: number;
    name: string;
  };
  // Add other relevant fields if needed (e.g., type TASK/JOB)
}

// API response structure for paginated tasks
interface PaginatedTasksResponse {
  items: TaskSummary[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
}

// API Response Types for the task list
interface TasksSuccessResponse {
    success: true; // Assuming the backend doesn't wrap tasks list in success flag
    items: TaskSummary[];
    total: number;
    page: number;
    perPage: number;
    totalPages: number;
}
interface TasksErrorResponse {
    success: false;
    error: string;
}
type TasksApiResponse = TasksSuccessResponse | TasksErrorResponse; // Adjust if backend structure differs

// --- Forum Topic Interfaces --- 
interface TopicSummary {
  id: number;
  title: string;
  replyCount?: number; // Optional
  lastActivityDate?: string; // Optional, assuming ISO string date
  // Add other relevant fields if needed
}

// API response structure for paginated topics (assuming similar to tasks)
interface PaginatedTopicsResponse {
  items: TopicSummary[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
}

// Potentially add specific success/error response types if needed
// type TopicsApiResponse = ...

const MapSidebar: React.FC<MapSidebarProps> = ({
  filters,
  onFiltersChange,
  showSidebar,
  onCloseMobile,
  onGoHomeClick
}): JSX.Element => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Fetch Network Stats
  const { 
    data: networkStats, 
    isLoading: isLoadingStats, 
    error: statsError 
  } = useQuery<NetworkStatsData, Error>(
    'networkStats', // Unique query key
    async () => {
      // Use the specific API response type
      const response = await api.get<NetworkStatsApiResponse>('/users/stats/network'); 
      if (response.data.success) {
        return response.data.data;
      } 
      // If success is false, message should exist
      throw new Error(response.data.message || 'Failed to fetch network stats'); 
    },
    {
      staleTime: 5 * 60 * 1000, // Refetch after 5 minutes
      // Only run query if user is logged in (as endpoint requires auth)
      enabled: !!user, 
    }
  );

  // Fetch Latest Tasks
  const {
    data: latestTasksData,
    isLoading: isLoadingLatestTasks,
    error: latestTasksError,
  } = useQuery<PaginatedTasksResponse, Error>(
    'latestTasksSidebar', // Use a distinct query key
    async () => {
      const response = await api.get<PaginatedTasksResponse>('/tasks', {
        params: { page: 1, per_page: 2 }, // Fetch page 1, limit 2
      });
      return response.data; 
    },
    {
      staleTime: 60 * 1000, // Refetch after 1 minute
      enabled: !!user, 
      refetchOnWindowFocus: false, 
    }
  );

  // Fetch Latest Forum Topics (NEW)
  const {
    data: latestTopicsData,
    isLoading: isLoadingLatestTopics,
    error: latestTopicsError,
  } = useQuery<PaginatedTopicsResponse, Error>(
    'latestTopicsSidebar', // Unique query key
    async () => {
      const response = await api.get<{ success: boolean; data?: PaginatedTopicsResponse; message?: string }>('/forum/topics', {
        params: { page: 1, per_page: 2, sort_by: 'lastActivity', order: 'desc' }, 
      });
      console.log('Sidebar Forum Topics API Response:', response.data);

      if (response.data.success && response.data.data) {
        // Return the nested data object which matches PaginatedTopicsResponse
        return response.data.data; 
      } else {
        // Throw an error if success is false or data is missing
        throw new Error(response.data.message || 'Failed to fetch topics or invalid response structure');
      }
    },
    {
      staleTime: 2 * 60 * 1000, // Refetch after 2 minutes
      refetchOnWindowFocus: false, 
      // Keep enabled: true unless forum requires login
    }
  );

  // Define items for the profession legend
  const professionLegendItems = [
    { key: 'technology', name: 'Technology' },
    { key: 'finance', name: 'Finance' },
    { key: 'healthcare', name: 'Healthcare' },
    { key: 'education', name: 'Education' },
    { key: 'engineering', name: 'Engineering' },
    { key: 'legal', name: 'Legal' },
    { key: 'marketing', name: 'Marketing' },
    { key: 'sales', name: 'Sales' },
    { key: 'human resources', name: 'Human Resources' },
    { key: 'arts', name: 'Arts' },
    { key: 'manufacturing', name: 'Manufacturing' },
    { key: 'retail', name: 'Retail' },
    { key: 'government', name: 'Government' },
    { key: 'energy', name: 'Energy' },
    { key: 'student', name: 'Student' },
    { key: 'retired', name: 'Retired' },
    { key: 'other', name: 'Other' }
  ];

  // Accordion state
  const [activeKeys, setActiveKeys] = useState<string[]>([
    'filter',
    'legend',
    'skillExchange',
    'taskHubs',
    'forum'
  ]);

  // Handle accordion toggle
  const handleAccordionToggle = (key: string) => {
    setActiveKeys(prevKeys => {
      if (prevKeys.includes(key)) {
        return prevKeys.filter(k => k !== key);
      } else {
        return [...prevKeys, key];
      }
    });
  };

  // Handle filter changes
  const handleProfessionFilterChange = (profession: string, checked: boolean) => {
    onFiltersChange({
      ...filters,
      professions: {
        ...filters.professions,
        [profession]: checked
      }
    });
  };

  const handleSelectAllProfessionsChange = (isChecked: boolean) => {
    const updatedProfessionsFilter: { [key: string]: boolean } = {};
    professionOptions.forEach(option => {
      updatedProfessionsFilter[option.value] = isChecked;
    });
    onFiltersChange({
      ...filters,
      professions: updatedProfessionsFilter,
    });
  };

  const handleExperienceFilterChange = (level: 'junior' | 'midLevel' | 'senior', checked: boolean) => {
    onFiltersChange({
      ...filters,
      experienceLevels: {
        ...filters.experienceLevels,
        [level]: checked
      }
    });
  };

  const handleInterestChange = (interest: string) => {
    onFiltersChange({
      ...filters,
      interest: interest
    });
  };

  const handleDistanceToggleChange = (checked: boolean) => {
    onFiltersChange({
      ...filters,
      isDistanceFilterEnabled: checked
    });
  };

  const handleDistanceChange = (value: number) => {
    onFiltersChange({
      ...filters,
      maxDistance: value
    });
  };

  const handleComingSoonClick = (featureName: string) => {
    showToast(`${featureName} feature coming soon!`, 'info');
  };

  const resetFilters = () => {
    onFiltersChange({
      ...filters,
      professions: {},
      experienceLevels: { junior: false, midLevel: false, senior: false },
      interest: '',
      isDistanceFilterEnabled: false,
      maxDistance: 0
    });
  };

  const areAllProfessionsSelected = professionOptions.every(opt => filters.professions[opt.value]);

  return (
    <div 
        className={`sidebar mobile-sidebar bg-white shadow-sm ${showSidebar ? 'show' : ''}`}
    >
      <div 
        className="sticky-top bg-white d-flex justify-content-between align-items-center p-3 border-bottom" 
        style={{ zIndex: 1020 }}
       >
        <h4 className="fw-bold mb-0">Control Panel</h4>
        {showSidebar && (
          <Button 
            variant="link" 
            className="btn-close" 
            onClick={onCloseMobile}
            aria-label="Close"
          />
        )}
      </div>
      
      <div className="flex-grow-1 overflow-y-auto p-3">
          <Card className="border-0 shadow-sm mb-3">
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <div 
                  className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3" 
                  style={{ width: '50px', height: '50px' }}
                >
                  <span className="fw-bold fs-5">{user?.name ? user.name[0] : '?'}</span>
                </div>
                <div>
                  <h5 className="mb-0 fw-bold">{user?.name || 'User'}</h5>
                  <p className="mb-0 text-muted">{user?.profession || 'Data Professional'}</p>
                </div>
              </div>
              <div className="d-grid">
                <Button 
                  variant="outline-primary" 
                  className="rounded-pill" 
                  href="/profile"
                >
                  <FontAwesomeIcon icon={faUser} className="me-2" /> View Your Profile
                </Button>
              </div>
            </Card.Body>
          </Card>
          
          <Accordion 
            className="shadow-sm mb-3" 
            activeKey={activeKeys}
            alwaysOpen
          >
            <Accordion.Item eventKey="filter">
              <Accordion.Header onClick={() => handleAccordionToggle('filter')}>
                <FontAwesomeIcon icon={faFilter} className="me-2" /> Filters
              </Accordion.Header>
              <Accordion.Body>
                <div className="mb-3">
                  <Form.Label className="fw-bold">Profession</Form.Label>
                  <Dropdown>
                    <Dropdown.Toggle
                      variant="outline-secondary"
                      id="professionDropdown"
                      className="w-100"
                      data-bs-auto-close="outside"
                    >
                      Select Professions
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100 p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      <Dropdown.Item as="div" onClick={e => e.stopPropagation()}>
                        <Form.Check
                          type="checkbox"
                          id="select-all-professions"
                          label="Select All"
                          checked={areAllProfessionsSelected}
                          onChange={(e) => handleSelectAllProfessionsChange(e.target.checked)}
                          className="profession-filter-checkbox fw-bold"
                        />
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      {professionOptions.map((option: SelectOption) => (
                        <Dropdown.Item key={option.value} as="div" onClick={e => e.stopPropagation()}>
                          <Form.Check
                            type="checkbox"
                            id={`profession-${option.value}`}
                            label={option.label}
                            checked={filters.professions[option.value] || false}
                            onChange={(e) => handleProfessionFilterChange(option.value, e.target.checked)}
                            className="profession-filter-checkbox"
                          />
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
                
                <div className="mb-3">
                  <Form.Label className="fw-bold">Experience Level</Form.Label>
                  <Form.Check 
                    type="checkbox" 
                    id="junior" 
                    label="Junior (0-3 years)" 
                    checked={filters.experienceLevels.junior} 
                    onChange={(e) => handleExperienceFilterChange('junior', e.target.checked)}
                    className="mb-2"
                  />
                  <Form.Check 
                    type="checkbox" 
                    id="midLevel" 
                    label="Mid-Level (4-7 years)" 
                    checked={filters.experienceLevels.midLevel} 
                    onChange={(e) => handleExperienceFilterChange('midLevel', e.target.checked)}
                    className="mb-2"
                  />
                  <Form.Check 
                    type="checkbox" 
                    id="senior" 
                    label="Senior (8+ years)" 
                    checked={filters.experienceLevels.senior} 
                    onChange={(e) => handleExperienceFilterChange('senior', e.target.checked)}
                  />
                </div>
                
                <div className="mb-3">
                  <Form.Label className="fw-bold">Interests</Form.Label>
                  <Form.Select 
                    value={filters.interest}
                    onChange={(e) => handleInterestChange(e.target.value)}
                  >
                    <option value="">All Interests</option>
                    <option value="mentoring">Mentoring</option>
                    <option value="projectCollaboration">Project Collaboration</option>
                    <option value="technicalDiscussions">Technical Discussions</option>
                    <option value="socializing">Socializing</option>
                    <option value="jobOpportunities">Job Opportunities</option>
                  </Form.Select>
                </div>
                
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="fw-bold mb-0">Distance</h6>
                    <Form.Check 
                      type="switch" 
                      id="distanceToggle" 
                      label="Filter by distance" 
                      checked={filters.isDistanceFilterEnabled} 
                      onChange={(e) => handleDistanceToggleChange(e.target.checked)}
                    />
                  </div>
                  <div 
                    id="distanceSliderContainer"
                    style={{
                      opacity: filters.isDistanceFilterEnabled ? 1 : 0.5,
                      pointerEvents: filters.isDistanceFilterEnabled ? 'auto' : 'none'
                    }}
                  >
                    <div className="range-container">
                      <Form.Range 
                        id="distanceFilter" 
                        min={1} 
                        max={500} 
                        step={1} 
                        value={filters.maxDistance} 
                        onChange={(e) => handleDistanceChange(parseInt(e.target.value))}
                        disabled={!filters.isDistanceFilterEnabled}
                      />
                      <div className="d-flex justify-content-between">
                        <span>1 km</span>
                        <span id="distanceValue">{filters.maxDistance} km</span>
                        <span>500 km</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <h6 className="fw-bold mb-2">Task/Job Involvement</h6>
                  <Form.Check 
                    type="checkbox" 
                    id="filter_has_open" 
                    label="Has Open Tasks/Jobs" 
                    className="mb-1"
                  />
                  <Form.Check 
                    type="checkbox" 
                    id="filter_is_recruiting" 
                    label="Currently Recruiting" 
                    className="mb-1"
                  />
                  <Form.Check 
                    type="checkbox" 
                    id="filter_is_participating" 
                    label="Participating in Tasks" 
                    className="mb-1"
                  />
                </div>

                <div className="mb-3">
                  <h6 className="fw-bold mb-2">Forum Activity</h6>
                  <Form.Check 
                    type="checkbox" 
                    id="filter_active_forum" 
                    label="Recently Active in Forum" 
                    className="mb-1"
                  />
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
          
          <Accordion 
            className="shadow-sm mb-3" 
            activeKey={activeKeys}
            alwaysOpen
          >
            <Accordion.Item eventKey="legend">
              <Accordion.Header onClick={() => handleAccordionToggle('legend')}>
                <FontAwesomeIcon icon={faMapSigns} className="me-2" /> Legend
              </Accordion.Header>
              <Accordion.Body>
                <div className="mb-3">
                  <h6 className="fw-bold small text-uppercase text-muted">Profession</h6>
                  <div className="row row-cols-2 g-2">
                    {professionLegendItems.map(item => (
                      <div className="col" key={item.key}>
                        <div className="legend-item d-flex align-items-center">
                          <div 
                            className="legend-color me-2" 
                            style={{
                               width: '15px', 
                               height: '15px', 
                               backgroundColor: getProfessionColor(item.key), 
                               borderRadius: '50%'
                             }}
                          ></div>
                          <small>{item.name}</small> 
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h6 className="fw-bold small text-uppercase text-muted">Experience</h6>
                  <div className="d-flex align-items-center mb-2">
                    <div style={{ width: '20px', height: '20px', borderRadius: '50%', border: '2px solid #000', marginRight: '8px' }}></div>
                    <span>Junior (0-3 years)</span>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <div style={{ width: '25px', height: '25px', borderRadius: '50%', border: '2px solid #000', marginRight: '8px' }}></div>
                    <span>Mid-Level (4-7 years)</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <div style={{ width: '30px', height: '30px', borderRadius: '50%', border: '2px solid #000', marginRight: '8px' }}></div>
                    <span>Senior (8+ years)</span>
                  </div>
                </div>
                
                <div className="mt-3">
                  <h6 className="fw-bold small text-uppercase text-muted">Special Markers</h6>
                  <div className="d-flex align-items-center mb-2">
                    <div style={{ width: '25px', height: '25px', borderRadius: '50%', backgroundColor: '#0d6efd', border: '2px solid white', marginRight: '8px' }}></div>
                    <span>Your Location</span>
                  </div>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
          
          <Card className="border-0 shadow-sm mb-3 network-insights">
            <Card.Body>
              <h5 className="fw-bold mb-3">Network Insights</h5>
              {isLoadingStats ? (
                <div className="text-center text-muted py-3">
                  <Spinner animation="border" size="sm" /> Loading Stats...
                </div>
              ) : statsError ? (
                 <div className="text-center text-danger py-3 small">
                   <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" /> 
                   Could not load stats: {statsError.message}
                 </div>
              ) : networkStats ? (
                <>
                  <div className="d-flex justify-content-around mb-3 text-center">
                    <div>
                      <h2 className="fw-bold text-primary mb-0">{networkStats.totalUsers}</h2>
                      <small className="text-muted">Total Users</small>
                    </div>
                    <div>
                      <h2 className="fw-bold text-primary mb-0">{networkStats.userConnections}</h2>
                      <small className="text-muted">Your Connections</small>
                    </div>
                    <div>
                      <h2 className="fw-bold text-primary mb-0">{networkStats.openTasks}</h2>
                      <small className="text-muted">Open Tasks</small>
                    </div>
                  </div>
                </>
              ) : (
                 <div className="text-center text-muted py-3 small">
                   Network stats unavailable.
                 </div>
              ) }
            </Card.Body>
          </Card>
          
          <Accordion 
            className="shadow-sm mb-3" 
            activeKey={activeKeys}
            alwaysOpen
          >
            <Accordion.Item eventKey="skillExchange">
              <Accordion.Header onClick={() => handleAccordionToggle('skillExchange')}>
                <FontAwesomeIcon icon={faExchangeAlt} className="me-2" /> Skill Exchange
              </Accordion.Header>
              <Accordion.Body>
                <Card className="skill-exchange-panel">
                  <Card.Body className="p-0">
                    <div className="p-3">
                      <p className="mb-3">Find professionals to learn from or teach skills to.</p>
                      <Button 
                        variant="primary" 
                        size="sm" 
                        className="w-100" 
                        onClick={() => handleComingSoonClick('Skill Exchange')}
                      >
                        View Skill Exchange
                      </Button>
                    </div>
                    <hr className="my-0" />
                    <div className="p-3">
                      <h6>Popular Skills Nearby</h6>
                      <div className="d-flex flex-wrap gap-1 mt-2">
                        <span className="badge bg-light text-dark">Python</span>
                        <span className="badge bg-light text-dark">Data Analysis</span>
                        <span className="badge bg-light text-dark">SQL</span>
                        <span className="badge bg-light text-dark">R</span>
                        <span className="badge bg-light text-dark">Machine Learning</span>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
          
          <Accordion 
            className="shadow-sm mb-3" 
            activeKey={activeKeys}
            alwaysOpen
          >
            <Accordion.Item eventKey="taskHubs">
              <Accordion.Header onClick={() => handleAccordionToggle('taskHubs')}>
                <FontAwesomeIcon icon={faTasks} className="me-2" /> Task/Job Hub
              </Accordion.Header>
              <Accordion.Body>
                <Card className="task-hub-panel">
                  <Card.Body className="p-0">
                    <div className="p-3">
                      <p className="mb-3">Discover tasks and job opportunities in your area.</p>
                      <Button variant="primary" size="sm" className="w-100" href="/tasks">
                        Browse All Tasks & Jobs
                      </Button>
                    </div>
                    <hr className="my-0" />
                    <div className="p-3">
                      <h6>Recent Opportunities</h6>
                      <div className="mt-2">
                        {isLoadingLatestTasks ? (
                          <div className="text-center text-muted py-2">
                            <Spinner animation="border" size="sm" /> Loading...
                          </div>
                        ) : latestTasksError ? (
                          <div className="text-center text-danger py-2 small">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                            Error loading tasks.
                          </div>
                        ) : latestTasksData && latestTasksData.items && latestTasksData.items.length > 0 ? (
                          latestTasksData.items.map(task => (
                            <div key={task.id} className="mb-2">
                              <Link 
                                to={`/tasks`}
                                state={{ expandTaskId: task.id }}
                                className="text-decoration-none" 
                                title={task.title}
                              >
                                <p className="mb-0 fw-bold text-truncate">{task.title}</p>
                                <small className="text-muted d-block">
                                  Posted by {task.owner?.name || 'Unknown'}
                                  {' • '}
                                  {new Date(task.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </small>
                              </Link>
                            </div>
                          ))
                        ) : (
                          <p className="text-muted small fst-italic mb-0">No recent opportunities found.</p>
                        )}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
          
          <Accordion 
            className="shadow-sm mb-3" 
            activeKey={activeKeys}
            alwaysOpen
          >
            <Accordion.Item eventKey="forum">
              <Accordion.Header onClick={() => handleAccordionToggle('forum')}>
                <FontAwesomeIcon icon={faComments} className="me-2" /> Community Forum
              </Accordion.Header>
              <Accordion.Body>
                <Card className="forum-panel">
                  <Card.Body className="p-0">
                    <div className="p-3">
                      <p className="mb-3">Join discussions with other data professionals.</p>
                      <Link to="/forum" className="d-grid text-decoration-none">
                        <Button variant="primary" size="sm" className="w-100">
                          Go to Forum
                        </Button>
                      </Link>
                    </div>
                    <hr className="my-0" />
                    <div className="p-3">
                      <h6>Hot Topics</h6>
                      <div className="mt-2">
                        {isLoadingLatestTopics ? (
                          <div className="text-center text-muted py-2">
                            <Spinner animation="border" size="sm" /> Loading...
                          </div>
                        ) : latestTopicsError ? (
                          <div className="text-center text-danger py-2 small">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                            Error loading topics.
                          </div>
                        ) : latestTopicsData && latestTopicsData.items && latestTopicsData.items.length > 0 ? (
                          latestTopicsData.items.map(topic => (
                            <div key={topic.id} className="mb-2">
                              <Link to={`/forum/topic/${topic.id}`} className="text-decoration-none" title={topic.title}>
                                <p className="mb-0 fw-bold text-truncate">{topic.title}</p>
                                <small className="text-muted d-block">
                                  {topic.replyCount !== undefined ? `${topic.replyCount} replies` : ''}
                                  {topic.replyCount !== undefined && topic.lastActivityDate ? ' • ' : ''}
                                  {topic.lastActivityDate ? 
                                    `${new Date(topic.lastActivityDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` 
                                    : ''}
                                </small> 
                              </Link>
                            </div>
                          ))
                        ) : (
                          <p className="text-muted small fst-italic mb-0">No recent topics found.</p>
                        )}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
      </div>
    </div>
  );
};

export default MapSidebar;
